/**
 * What a deal is WORTH on screen.
 *
 * Owner's ruling, 2026-09-10: a deal's value is its LINE-ITEM TOTAL whenever it
 * has lines, and the hand-typed `amount` only when it has none. Every surface
 * that shows a deal's money — the list's Amount column, its Expected column,
 * the detail page's forecast — reads the same answer from here.
 *
 * ⚠️ **ONE FUNCTION, DELIBERATELY, RATHER THAN AN INLINE `??` PER SITE.** Three
 * copies of `deal.linesTotal ?? deal.amount` agree the day they are written and
 * drift the first time one of them is edited — and the drift is silent, because
 * each copy still renders a plausible figure in the right currency. There is
 * nothing to compare, no error, and the only symptom is two screens quoting
 * different money for one deal.
 */
import type { Deal } from '@/lib/types';

/**
 * The figure to SHOW for a deal: its lines' total when it has lines, otherwise
 * the typed amount. An exact DECIMAL string, or `null` when the deal has
 * neither.
 *
 * ⚠️ **`linesTotal === null` is "no lines", NOT "the lines come to nothing".**
 * That is why this is `??` and not `||`: a deal whose lines genuinely total
 * zero reads `"0.00"`, which is falsy-adjacent in every other language and
 * would fall through to the typed amount under `||`, showing a figure the sale
 * no longer stands at.
 *
 * ⚠️ **`null` out of here is "not priced", never "worth nothing".** It carries
 * `amount`'s own contract forward: an unpriced deal and a deal worth zero are
 * different facts, so callers render an empty state rather than `0.00`.
 *
 * ⚠️ **Never the value to EDIT.** The writable column is `amount`, and it is
 * the only one of the two a form can express — `linesTotal` is derived per read
 * and a deal's lines are edited as lines. A control that seeds its draft from
 * here would write a derived figure into the typed column and make the two
 * agree by overwriting one of them.
 *
 * Typed against the pair rather than the whole record so it stays usable on a
 * projection — and so a `KanbanCard`, which carries `amount` and no
 * `linesTotal`, is a compile error here rather than a board that silently keeps
 * showing the typed figure.
 */
export function dealValue(deal: Pick<Deal, 'amount' | 'linesTotal'>): string | null {
  return deal.linesTotal ?? deal.amount;
}

/**
 * Is the figure `dealValue` returns coming from the LINES rather than from the
 * typed amount, on a deal where the two would otherwise be confused?
 *
 * True only when the deal has lines AND nobody has typed an amount — which is
 * the one case where a reader sees a number in a field whose editor is empty,
 * and the one that needs saying out loud. When both are present the editor
 * shows what the editor writes and there is nothing surprising to caption; when
 * neither is, there is no figure at all.
 */
export function dealValueIsFromLines(deal: Pick<Deal, 'amount' | 'linesTotal'>): boolean {
  /*
   * ⚠️ **A TYPE TEST, NOT `!== null`, AND THE DIFFERENCE IS A REAL WINDOW.**
   * The server deploys before the browser app, so between the two there are
   * responses in flight with NO `linesTotal` key at all — the field is
   * `undefined`, which the declared type says cannot happen and which
   * `!== null` answers TRUE for. That would caption an unpriced deal with
   * "from the products below" on a deal that has no products.
   *
   * Nothing renders it today, because every call site sits inside a branch
   * `dealValue` has already sent down the not-priced path — but that is the
   * render guarding the predicate rather than the predicate being right, and
   * the next call site will not know to do it.
   */
  return typeof deal.linesTotal === 'string' && deal.amount === null;
}
